import { jsonObjectSchema, type JsonObject } from "@arti/dashboard-events";

import type { DashboardTaskEvent } from "../dashboard-client";
import type { ProviderProgressEntry } from "./useProviderProgress";

const USAGE_UPDATE_EVENT_TYPE = "usage:update";

export function toProviderProgressEventData(value: unknown): JsonObject {
  const parsed = jsonObjectSchema.safeParse(value);
  return parsed.success ? parsed.data : {};
}

export function normalizePersistedProviderProgressEvent(
  event: DashboardTaskEvent,
): ProviderProgressEntry {
  return {
    event_data: toProviderProgressEventData(event.payload),
    event_type: event.event_type,
    execution_request_id: null,
    node_id: null,
    received_at: event.created_at,
    stream_id: event.stream_id ?? "main",
    task_id: event.task_id,
    timestamp: event.created_at,
  };
}

export function appendProviderProgressEntry(
  existing: ProviderProgressEntry[],
  entry: ProviderProgressEntry,
): ProviderProgressEntry[] {
  if (entry.event_type !== USAGE_UPDATE_EVENT_TYPE) {
    return [...existing, entry];
  }

  const withoutPreviousUsageUpdates = existing.filter(
    (candidate) =>
      !(
        candidate.event_type === USAGE_UPDATE_EVENT_TYPE &&
        candidate.stream_id === entry.stream_id
      ),
  );

  return [...withoutPreviousUsageUpdates, entry];
}

export function compactProviderProgressEntries(
  entries: ProviderProgressEntry[],
): ProviderProgressEntry[] {
  return entries.reduce<ProviderProgressEntry[]>(
    (accumulator, entry) => appendProviderProgressEntry(accumulator, entry),
    [],
  );
}

export function buildHydratedProviderProgressEntries(
  events: DashboardTaskEvent[],
): ProviderProgressEntry[] {
  return compactProviderProgressEntries(
    events.map(normalizePersistedProviderProgressEvent),
  );
}
